/**
 * Authentication service for handling user login, registration, and token management
 */

import { User } from '../types';

// API base URL
const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5003/api';

// Interface for login request
interface LoginRequest {
  email: string;
  password: string;
}

// Interface for registration request
interface RegisterRequest {
  firstName: string;
  lastName: string;
  email: string;
  password: string;
  school: string;
  grade: string;
}

// Interface for auth response
interface AuthResponse {
  token: string;
  user: User;
}

/**
 * Login a user with email and password
 */
export const login = async (credentials: LoginRequest): Promise<AuthResponse> => {
  try {
    const response = await fetch(`${API_URL}/auth/login`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(credentials),
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.message || 'Login failed');
    }

    const data = await response.json();
    
    // Store token in localStorage
    localStorage.setItem('token', data.token);
    
    return data;
  } catch (error) {
    throw error;
  }
};

/**
 * Register a new user
 */
export const register = async (userData: RegisterRequest): Promise<AuthResponse> => {
  try {
    const response = await fetch(`${API_URL}/auth/register`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        first_name: userData.firstName,
        last_name: userData.lastName,
        email: userData.email,
        password: userData.password,
        school: userData.school,
        grade: userData.grade,
      }),
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.message || 'Registration failed');
    }

    const data = await response.json();
    
    // Store token in localStorage
    localStorage.setItem('token', data.token);
    
    return data;
  } catch (error) {
    throw error;
  }
};

/**
 * Logout the current user
 */
export const logout = (): void => {
  localStorage.removeItem('token');
};

/**
 * Check if user is authenticated
 */
export const isAuthenticated = (): boolean => {
  return localStorage.getItem('token') !== null;
};

/**
 * Get the current authentication token
 */
export const getToken = (): string | null => {
  return localStorage.getItem('token');
};

/**
 * Mock login for development (when backend is not available)
 */
export const mockLogin = async (credentials: LoginRequest): Promise<AuthResponse> => {
  // Simulate API call delay
  await new Promise(resolve => setTimeout(resolve, 1000));
  
  // Check credentials (for demo purposes)
  if (credentials.email === 'admin@professorconnect.com' && credentials.password === 'admin123') {
    const mockResponse: AuthResponse = {
      token: 'mock-jwt-token',
      user: {
        id: 1,
        email: 'admin@professorconnect.com',
        first_name: 'Admin',
        last_name: 'User',
        full_name: 'Admin User',
        role: 'admin',
        // Aliases for camelCase consistency
        firstName: 'Admin',
        lastName: 'User'
      }
    };
    
    localStorage.setItem('token', mockResponse.token);
    return mockResponse;
  }
  
  if (credentials.email === 'student@example.com' && credentials.password === 'password1') {
    const mockResponse: AuthResponse = {
      token: 'mock-jwt-token',
      user: {
        id: 2,
        email: 'student@example.com',
        first_name: 'John',
        last_name: 'Doe',
        full_name: 'John Doe',
        role: 'student',
        // Aliases for camelCase consistency
        firstName: 'John',
        lastName: 'Doe'
      }
    };
    
    localStorage.setItem('token', mockResponse.token);
    return mockResponse;
  }
  
  throw new Error('Invalid email or password');
};

/**
 * Mock registration for development (when backend is not available)
 */
export const mockRegister = async (userData: RegisterRequest): Promise<AuthResponse> => {
  // Simulate API call delay
  await new Promise(resolve => setTimeout(resolve, 1000));
  
  // For demo purposes, always succeed
  const mockResponse: AuthResponse = {
    token: 'mock-jwt-token',
    user: {
      id: Math.floor(Math.random() * 1000) + 10, // Random ID
      email: userData.email,
      first_name: userData.firstName,
      last_name: userData.lastName,
      full_name: `${userData.firstName} ${userData.lastName}`,
      role: 'student',
      school: userData.school,
      grade: userData.grade,
      // Aliases for camelCase consistency
      firstName: userData.firstName,
      lastName: userData.lastName
    }
  };
  
  localStorage.setItem('token', mockResponse.token);
  return mockResponse;
};
